/**
 * The site's read side of the CMS content snapshot.
 *
 * The CMS publishes all public content as versioned JSON in Vercel Blob, one
 * small file per collection part, so no fetch exceeds Next's 2 MB Data Cache
 * limit and the browser downloads only what a read needs:
 *
 *   <NEXT_PUBLIC_CONTENT_SNAPSHOT_BASE>/content/latest.json
 *       {version, publishedAt, locales, parts: {[locale]: {[collection]: n}}}
 *   <base>/content/<version>/<locale>/<collection>/<i>.json
 *       JSON array of docs, part i of n (0-based, each <= 1.5 MB); parts
 *       0..n-1 concatenated are the collection in export order
 *       (`diseasesContent` is a collection like any other)
 *   <base>/content/<version>/<locale>/globals.json
 *       {<global slug>: doc}
 *   <base>/content/<version>/diseases-slug-map.json
 *       the diseasesSlugMap array (locale-independent)
 *
 * A read fetches the pointer (cached under the `content` tag, one day) and
 * then only the files the path needs (each cached under `content` and
 * `content-<version>`); parsed files are kept in process memory per version.
 * A new version replaces the old copies; the webhook for `content-snapshot`
 * expires the `content` tag so the pointer is re-read. A locale or collection
 * the pointer does not list is a `ContentSnapshotError`, never an empty read.
 *
 * Client-safe (plain `fetch`): the browser path reads the same public files.
 */

import {
  parseCmsPath,
  selectFromSnapshot,
  joinFallbackLocale,
  sourceNeeds,
  selectGlobalFromSnapshot,
  snapshotCovers,
  type ContentSnapshot,
  type SelectResult,
  type SnapshotDoc,
} from "./content-selectors"

interface LatestPointer {
  version: string
  publishedAt: string
  locales: string[]
  parts: Record<string, Record<string, number>>
}

export type ContentSnapshotErrorKind =
  | "not-configured"
  | "latest-unavailable"
  | "locale-unavailable"
  | "locale-not-published"
  | "collection-not-published"

export class ContentSnapshotError extends Error {
  readonly kind: ContentSnapshotErrorKind
  constructor(kind: ContentSnapshotErrorKind, message: string) {
    super(message)
    this.name = "ContentSnapshotError"
    this.kind = kind
  }
}

/**
 * The PIM view a snapshot-answerable path is toggled under: `content:` plus
 * its collection slug, `content:globals` for any global.
 */
export function contentView(path: string): string {
  const { resource } = parseCmsPath(path)
  return `content:${resource.startsWith("globals/") ? "globals" : resource}`
}

function snapshotBase(): string {
  const base = process.env.NEXT_PUBLIC_CONTENT_SNAPSHOT_BASE
  if (!base) {
    throw new ContentSnapshotError(
      "not-configured",
      "NEXT_PUBLIC_CONTENT_SNAPSHOT_BASE is not set"
    )
  }
  return base.replace(/\/+$/, "")
}

/** One retry, then give up with the reason. */
async function fetchJson<T>(
  url: string,
  init: { next: { tags: string[]; revalidate?: number } },
  kind: ContentSnapshotErrorKind
): Promise<T> {
  let reason = ""
  for (let attempt = 0; attempt < 2; attempt++) {
    const res = await fetch(url, init).catch((err: unknown) => err)
    if (res instanceof Response) {
      if (res.ok) return (await res.json()) as T
      reason = `HTTP ${res.status}`
    } else {
      reason = res instanceof Error ? res.message : String(res)
    }
  }
  throw new ContentSnapshotError(kind, `${url}: ${reason}`)
}

/** Parsed files of the newest version seen, by URL; replaced on a new version. */
let held: { version: string; files: Map<string, Promise<unknown>> } = {
  version: "",
  files: new Map(),
}

/** Test seam — the held copies are module state. */
export function __resetContentStoreForTests(): void {
  held = { version: "", files: new Map() }
}

async function loadLatest(base: string): Promise<LatestPointer> {
  const latest = await fetchJson<LatestPointer>(
    `${base}/content/latest.json`,
    { next: { tags: ["content"], revalidate: 86_400 } },
    "latest-unavailable"
  )
  if (held.version !== latest.version) {
    held = { version: latest.version, files: new Map() }
  }
  return latest
}

function loadFile<T>(base: string, latest: LatestPointer, file: string) {
  const url = `${base}/content/${latest.version}/${file}`
  const { files } = held
  const existing = files.get(url)
  if (existing) return existing as Promise<T>
  const pending = fetchJson<T>(
    url,
    { next: { tags: ["content", `content-${latest.version}`] } },
    "locale-unavailable"
  )
  files.set(url, pending)
  // A failed load must not be held: the next caller tries again.
  pending.catch(() => {
    if (files.get(url) === pending) files.delete(url)
  })
  return pending
}

function partCount(
  latest: LatestPointer,
  locale: string,
  collection: string
): number | undefined {
  if (!latest.locales.includes(locale) || !latest.parts?.[locale]) {
    throw new ContentSnapshotError(
      "locale-not-published",
      `snapshot ${latest.version} has no ${locale} files`
    )
  }
  return latest.parts[locale][collection]
}

async function loadCollection(
  base: string,
  latest: LatestPointer,
  locale: string,
  collection: string
): Promise<SnapshotDoc[]> {
  const count = partCount(latest, locale, collection)
  if (!count) {
    throw new ContentSnapshotError(
      "collection-not-published",
      `snapshot ${latest.version} has no ${locale} ${collection} parts`
    )
  }
  const parts = await Promise.all(
    Array.from({ length: count }, (_, i) =>
      loadFile<SnapshotDoc[]>(base, latest, `${locale}/${collection}/${i}.json`)
    )
  )
  return parts.flat()
}

/** What one read needs from a locale's files. */
export interface SnapshotNeeds {
  collections?: string[]
  /** Also load these when the locale lists them (no error if it does not). */
  optionalCollections?: string[]
  /** The first of these the locale lists (an error if it lists none). */
  firstOf?: string[]
  globals?: boolean
  slugMap?: boolean
}

/**
 * A {@link ContentSnapshot} holding just the files `needs` names, for one
 * locale; the selectors treat collections it lacks as absent.
 */
export async function loadSnapshot(
  cmsLocale: string,
  needs: SnapshotNeeds
): Promise<ContentSnapshot> {
  const base = snapshotBase()
  const latest = await loadLatest(base)
  partCount(latest, cmsLocale, "") // locale must be published
  const snapshot: ContentSnapshot = {
    version: latest.version,
    locale: cmsLocale,
    generatedAt: latest.publishedAt,
    collections: {},
    globals: {},
  }
  const first = needs.firstOf
    ? [
        needs.firstOf.find((c) => partCount(latest, cmsLocale, c)) ??
          needs.firstOf[needs.firstOf.length - 1],
      ]
    : []
  const optional = (needs.optionalCollections ?? []).filter((c) =>
    partCount(latest, cmsLocale, c)
  )
  await Promise.all([
    ...[...(needs.collections ?? []), ...optional, ...first].map(async (c) => {
      snapshot.collections[c] = await loadCollection(base, latest, cmsLocale, c)
    }),
    needs.globals &&
      loadFile<Record<string, SnapshotDoc>>(
        base,
        latest,
        `${cmsLocale}/globals.json`
      ).then((g) => {
        snapshot.globals = g
      }),
    needs.slugMap &&
      loadFile<SnapshotDoc[]>(base, latest, "diseases-slug-map.json").then(
        (m) => {
          snapshot.diseasesSlugMap = m
        }
      ),
  ])
  return snapshot
}

/**
 * Whether a CMS path can be answered from the snapshot at all. Collections the
 * snapshot does not carry (and `locale=all` on anything but the diseases) stay
 * live CMS reads.
 */
export function snapshotAnswers(path: string): boolean {
  return snapshotCovers(parseCmsPath(path))
}

/** `{docs, totalDocs}` for a collection path, from the snapshot. */
export async function readCmsPathFromSnapshot(
  path: string
): Promise<SelectResult> {
  const parsed = parseCmsPath(path)
  const needs = sourceNeeds(parsed)
  const locale = needs.slugMap ? "en" : parsed.locale
  const fallback = parsed.fallbackLocale
  const snapshot = await loadSnapshot(
    locale,
    needs.slugMap
      ? { slugMap: true }
      : {
          collections: [needs.collection],
          optionalCollections: [
            ...(needs.contentRead ? ["diseasesContent"] : []),
            ...(needs.collection === "diseases" && locale !== "en"
              ? ["biomarkersContent"]
              : []),
          ],
        }
  )
  const fallbackSnapshot =
    needs.borrowedFallback &&
    snapshot.collections.diseasesContent &&
    fallback &&
    fallback !== parsed.locale
      ? await loadSnapshot(fallback, {
          firstOf: ["diseasesContent", "diseases"],
        })
      : undefined
  const joinLocale =
    needs.collection === "diseases" && locale !== "en"
      ? joinFallbackLocale(parsed)
      : undefined
  const joinFallbackSnapshot = joinLocale
    ? joinLocale === fallback && fallbackSnapshot
      ? fallbackSnapshot
      : await loadSnapshot(
          joinLocale,
          joinLocale === "en"
            ? { collections: ["biomarkers"] }
            : { firstOf: ["biomarkersContent", "biomarkers"] }
        )
    : undefined
  return selectFromSnapshot(snapshot, path, {
    fallbackSnapshot,
    joinFallbackSnapshot,
  })
}

/** A global's document (`/globals/<slug>?depth=0`), from the snapshot. */
export async function readGlobalFromSnapshot(
  path: string
): Promise<SnapshotDoc> {
  return selectGlobalFromSnapshot(
    await loadSnapshot("en", { globals: true }),
    path
  )
}
